import { createRotatedImageBlob } from 'utils/image_util';
import { fetchPhotoBlob } from 'utils/image_load';
import activeEditor from 'domains/active_editor';

import selectors from './crops.selectors';
import slice from './crops.slice';
import utils from './crops.utils';

import type { AppThunk } from 'store/wos_store';

const { setActiveImageNode, setCropPhotoLoaded, createCrop, deleteCrop, rotateCrop } =
  slice.actions;

export function setNextActiveImageNode(): AppThunk {
  return function nextActiveImageNodeThunk(dispatch, getState) {
    const state = getState();

    const activeCanvasId = activeEditor.selectors.canvasId(state);
    const activeProduct = activeEditor.selectors.product(state);
    const cropsById = selectors.selectEntities(state);

    if (!activeProduct || !activeCanvasId) return;

    const cropId = utils.findNextAvailableCropId(cropsById, activeProduct, activeCanvasId);

    if (cropId) {
      const { nodeUid } = utils.parseCropId(cropId);
      dispatch(setActiveImageNode(nodeUid));
    }
  };
}

export function applyPhotoToActiveNode(photoId: number): AppThunk {
  return function applyPhotoThunk(dispatch, getState) {
    const state = getState();

    const activeImageNode = selectors.activeImageNode(state);
    const activeCanvasId = activeEditor.selectors.canvasId(state);

    if (!activeImageNode || !activeCanvasId) return;

    dispatch(createCrop({ canvasId: activeCanvasId, nodeUid: activeImageNode, photoId }));
    dispatch(setNextActiveImageNode());
  };
}

export function rotateActiveImage(): AppThunk {
  return async function rotateThunk(dispatch, getState) {
    const state = getState();
    const activeCrop = selectors.activeCrop(state);
    const oldSrc = activeCrop?.blobSrc;

    if (!activeCrop || !oldSrc) return;

    dispatch(setCropPhotoLoaded({ id: activeCrop.id, loaded: false }));

    // Have to resolve the object url back to a blob
    const oldBlob = await fetchPhotoBlob(oldSrc);

    // Since the blobSrc reflects the rotation, we reset back to 0 if we're rotating from 270
    const rotation = activeCrop.rotation === 270 ? -270 : 90;

    try {
      // Get the blob of the rotated canvas and revoke the previous one
      const blob = await createRotatedImageBlob(oldBlob, rotation);
      const newSrc = URL.createObjectURL(blob);
      URL.revokeObjectURL(oldSrc);
      dispatch(
        rotateCrop({
          cropId: activeCrop.id,
          rotation: activeCrop.rotation + rotation,
          blobSrc: newSrc,
        })
      );
    } catch (error) {
      // Keep the crop state the same if something in the rotation failed
      dispatch(setCropPhotoLoaded({ id: activeCrop.id, loaded: true }));
    }
  };
}

export function reconcileCrops(): AppThunk {
  return function reconcileThunk(dispatch, getState) {
    const state = getState();

    const potentialCropIds = selectors.potentialCropIds(state);
    const activeNodeUid = selectors.activeImageNode(state);
    const potentialNodeUids = potentialCropIds.map((id) => utils.parseCropId(id).nodeUid);
    const cropIds = selectors.selectIds(state);
    let deleted = false;

    // Remove crops with IDs that are no longer available
    cropIds.forEach((id: string) => {
      if (potentialCropIds.indexOf(id) === -1) {
        deleted = true;
        dispatch(deleteCrop(id));
      }
    });

    // If we deleted a crop, we should set the active image node since at least one node will be empty
    // Also set next active if the current active is not available
    if (deleted || !activeNodeUid || potentialNodeUids.indexOf(activeNodeUid) === -1) {
      dispatch(setNextActiveImageNode());
    }
  };
}
